import { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Grid,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
} from '@mui/material'
import { Add, Edit, Delete, Person, Business, Security, Send, Refresh, Cancel } from '@mui/icons-material'
import { PageHeader, Card, Button, DataTable, Modal, StatusChip } from '@components/common'
import { useAuth } from '@context/AuthContext'
import api from '@services/api'
import { toast } from 'react-toastify'

// User Management Tab
const UsersTab = () => {
  const [users, setUsers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [departments, setDepartments] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [inviteFormOpen, setInviteFormOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [activeSubTab, setActiveSubTab] = useState(0)
  const [editFormData, setEditFormData] = useState({
    department: '',
    job_title: '',
    phone_number: '',
    is_technician: false,
    is_active: true,
    role_ids: [],
  })
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    department: '',
    job_title: '',
    phone_number: '',
    is_technician: false,
    is_staff: false,
    role_ids: [],
  })
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [usersRes, invitesRes, deptsRes, rolesRes] = await Promise.all([
        api.get('/common/profiles/'),
        api.get('/auth/invitations/'),
        api.get('/common/departments/'),
        api.get('/common/roles/'),
      ])
      setUsers(usersRes.data.results || usersRes.data)
      setInvitations(invitesRes.data.results || invitesRes.data)
      setDepartments(deptsRes.data.results || deptsRes.data)
      setRoles(rolesRes.data.results || rolesRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleInviteUser = () => {
    setInviteFormData({
      email: '',
      first_name: '',
      last_name: '',
      department: '',
      job_title: '',
      phone_number: '',
      is_technician: false,
      is_staff: false,
      role_ids: [],
    })
    setInviteFormOpen(true)
  }

  const handleEdit = (profile) => {
    setSelectedUser(profile)
    setEditFormData({
      department: profile.department || '',
      job_title: profile.job_title || '',
      phone_number: profile.phone_number || '',
      is_technician: profile.is_technician || false,
      is_active: profile.user?.is_active ?? true,
      role_ids: profile.roles?.map((r) => r.id) || [],
    })
    setEditFormOpen(true)
  }

  const handleSaveEdit = async () => {
    setSaving(true)
    try {
      await api.patch(`/common/profiles/${selectedUser.id}/`, {
        department: editFormData.department || null,
        job_title: editFormData.job_title,
        phone_number: editFormData.phone_number,
        is_technician: editFormData.is_technician,
        is_active: editFormData.is_active,
        role_ids: editFormData.role_ids,
      })
      toast.success('User updated successfully')
      setEditFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving user:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || 'Failed to update user. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleSendInvitation = async () => {
    setSaving(true)
    try {
      await api.post('/auth/invitations/', {
        email: inviteFormData.email,
        first_name: inviteFormData.first_name,
        last_name: inviteFormData.last_name,
        department: inviteFormData.department || null,
        job_title: inviteFormData.job_title,
        phone_number: inviteFormData.phone_number,
        is_technician: inviteFormData.is_technician,
        is_staff: inviteFormData.is_staff,
        role_ids: inviteFormData.role_ids,
      })
      toast.success('Invitation sent successfully!')
      setInviteFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error sending invitation:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.email?.[0]
        || 'Failed to send invitation. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleResendInvitation = async (invitation) => {
    try {
      await api.post(`/auth/invitations/${invitation.id}/resend/`)
      toast.success('Invitation resent successfully!')
    } catch (error) {
      toast.error('Failed to resend invitation.')
    }
  }

  const handleCancelInvitation = async (invitation) => {
    try {
      await api.post(`/auth/invitations/${invitation.id}/cancel/`)
      toast.success('Invitation cancelled.')
      fetchData()
    } catch (error) {
      toast.error('Failed to cancel invitation.')
    }
  }

  const handleDelete = async () => {
    try {
      await api.delete(`/common/users/${selectedUser.user.id}/`)
      setDeleteModalOpen(false)
      toast.success('User deleted successfully')
      fetchData()
    } catch (error) {
      console.error('Error deleting user:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || 'Failed to delete user. Please try again.'
      toast.error(errorMessage)
    }
  }

  const userColumns = [
    {
      field: 'user',
      headerName: 'Name',
      renderCell: (value) => `${value?.first_name || ''} ${value?.last_name || ''}`.trim() || value?.username,
    },
    { field: 'user', headerName: 'Username', renderCell: (value) => value?.username },
    { field: 'user', headerName: 'Email', renderCell: (value) => value?.email || '-' },
    { field: 'department_name', headerName: 'Department', renderCell: (value) => value || '-' },
    { field: 'job_title', headerName: 'Job Title', renderCell: (value) => value || '-' },
    {
      field: 'is_technician',
      headerName: 'Technician',
      renderCell: (value) => value ? <Chip label="Yes" size="small" color="primary" /> : <Chip label="No" size="small" />,
    },
    {
      field: 'roles',
      headerName: 'Roles',
      renderCell: (value) => value?.map((r) => r.name).join(', ') || '-',
    },
    {
      field: 'user',
      headerName: 'Status',
      renderCell: (value) => (
        <StatusChip status={value?.is_active ? 'active' : 'inactive'} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setSelectedUser(row); setDeleteModalOpen(true) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const invitationColumns = [
    { field: 'email', headerName: 'Email' },
    {
      field: 'name',
      headerName: 'Name',
      renderCell: (_, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
    },
    { field: 'department_name', headerName: 'Department', renderCell: (value) => value || '-' },
    { field: 'job_title', headerName: 'Job Title', renderCell: (value) => value || '-' },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (value) => (
        <Chip
          label={value}
          size="small"
          color={value === 'pending' ? 'warning' : value === 'accepted' ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Sent',
      renderCell: (value) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => row.status === 'pending' && (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            title="Resend invitation"
            onClick={(e) => { e.stopPropagation(); handleResendInvitation(row) }}
          >
            <Refresh fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            title="Cancel invitation"
            onClick={(e) => { e.stopPropagation(); handleCancelInvitation(row) }}
          >
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const pendingInvitations = invitations.filter(i => i.status === 'pending')

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={activeSubTab} onChange={(_, v) => setActiveSubTab(v)}>
          <Tab label={`Users (${users.length})`} />
          <Tab label={`Pending Invitations (${pendingInvitations.length})`} />
        </Tabs>
        <Button startIcon={<Send />} onClick={handleInviteUser}>Invite User</Button>
      </Box>

      {activeSubTab === 0 && (
        <DataTable
          columns={userColumns}
          data={users}
          loading={loading}
          searchPlaceholder="Search users..."
          emptyMessage="No users found"
        />
      )}

      {activeSubTab === 1 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Users will receive an email invitation and must set their own password when accepting.
          </Alert>
          <DataTable
            columns={invitationColumns}
            data={pendingInvitations}
            loading={loading}
            searchPlaceholder="Search invitations..."
            emptyMessage="No pending invitations"
          />
        </>
      )}

      {/* Invite User Modal */}
      <Modal
        open={inviteFormOpen}
        onClose={() => setInviteFormOpen(false)}
        title="Invite New User"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setInviteFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSendInvitation} loading={saving} startIcon={<Send />}>Send Invitation</Button>
          </>
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          The user will receive an email with a link to set up their account and create their own password.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteFormData.email}
              onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
              required
              helperText="Invitation will be sent to this email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={inviteFormData.first_name}
              onChange={(e) => setInviteFormData({ ...inviteFormData, first_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={inviteFormData.last_name}
              onChange={(e) => setInviteFormData({ ...inviteFormData, last_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={inviteFormData.department}
                onChange={(e) => setInviteFormData({ ...inviteFormData, department: e.target.value })}
                label="Department"
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={inviteFormData.job_title}
              onChange={(e) => setInviteFormData({ ...inviteFormData, job_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={inviteFormData.phone_number}
              onChange={(e) => setInviteFormData({ ...inviteFormData, phone_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteFormData.is_technician}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, is_technician: e.target.checked })}
                />
              }
              label="Is Technician"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={inviteFormData.role_ids}
                onChange={(e) => setInviteFormData({ ...inviteFormData, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={inviteFormData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteFormData.is_staff}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, is_staff: e.target.checked })}
                />
              }
              label="Admin Access (Staff)"
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        title="Edit User"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} loading={saving}>Update</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={editFormData.department}
                onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                label="Department"
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={editFormData.job_title}
              onChange={(e) => setEditFormData({ ...editFormData, job_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={editFormData.phone_number}
              onChange={(e) => setEditFormData({ ...editFormData, phone_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_technician}
                  onChange={(e) => setEditFormData({ ...editFormData, is_technician: e.target.checked })}
                />
              }
              label="Is Technician"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={editFormData.role_ids}
                onChange={(e) => setEditFormData({ ...editFormData, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={editFormData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_active}
                  onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                  color={editFormData.is_active ? 'success' : 'default'}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Account Active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {editFormData.is_active
                      ? 'User can log in and access the system'
                      : 'User is blocked from logging in'}
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete User"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>Delete</Button>
          </>
        }
      >
        <Typography>Are you sure you want to delete "{selectedUser?.user?.username}"?</Typography>
      </Modal>
    </>
  )
}

// Department Management Tab
const DepartmentsTab = () => {
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedDept, setSelectedDept] = useState(null)
  const [formData, setFormData] = useState({ name: '', code: '', description: '', is_active: true })
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      const response = await api.get('/common/departments/')
      setDepartments(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching departments:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedDept(null)
    setFormData({ name: '', code: '', description: '', is_active: true })
    setFormOpen(true)
  }

  const handleEdit = (dept) => {
    setSelectedDept(dept)
    setFormData({
      name: dept.name,
      code: dept.code,
      description: dept.description || '',
      is_active: dept.is_active,
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (selectedDept) {
        await api.patch(`/common/departments/${selectedDept.id}/`, formData)
        toast.success('Department updated successfully')
      } else {
        await api.post('/common/departments/', formData)
        toast.success('Department created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving department:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.code?.[0]
        || error.response?.data?.name?.[0]
        || 'Failed to save department. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'code', headerName: 'Code', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    { field: 'member_count', headerName: 'Members' },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Department</Button>
      </Box>
      <DataTable
        columns={columns}
        data={departments}
        loading={loading}
        searchPlaceholder="Search departments..."
        emptyMessage="No departments found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedDept ? 'Edit Department' : 'Add Department'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedDept ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Department Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// Roles Management Tab
const RolesTab = () => {
  const [roles, setRoles] = useState([])
  const [availablePermissions, setAvailablePermissions] = useState({})
  const [permissionCategories, setPermissionCategories] = useState({})
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true, permissions: [] })
  const [saving, setSaving] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({})

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/common/roles/'),
        api.get('/auth/permissions/available/'),
      ])
      setRoles(rolesRes.data.results || rolesRes.data)
      setAvailablePermissions(permsRes.data.permissions || {})
      setPermissionCategories(permsRes.data.categories || {})
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedRole(null)
    setFormData({ name: '', description: '', is_active: true, permissions: [] })
    setExpandedCategories({})
    setFormOpen(true)
  }

  const handleEdit = (role) => {
    setSelectedRole(role)
    setFormData({
      name: role.name,
      description: role.description || '',
      is_active: role.is_active,
      permissions: role.permissions || [],
    })
    setExpandedCategories({})
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (selectedRole) {
        await api.patch(`/common/roles/${selectedRole.id}/`, formData)
        toast.success('Role updated successfully')
      } else {
        await api.post('/common/roles/', formData)
        toast.success('Role created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving role:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || 'Failed to save role. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const togglePermission = (permCode) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permCode)
        ? prev.permissions.filter((p) => p !== permCode)
        : [...prev.permissions, permCode],
    }))
  }

  const toggleCategory = (categoryPerms) => {
    const permCodes = categoryPerms.map((p) => p.code)
    const allSelected = permCodes.every((p) => formData.permissions.includes(p))
    if (allSelected) {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => !permCodes.includes(p)),
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...permCodes])],
      }))
    }
  }

  const columns = [
    { field: 'name', headerName: 'Role Name', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    {
      field: 'permissions',
      headerName: 'Permissions',
      renderCell: (value) => (
        <Chip label={`${value?.length || 0} permissions`} size="small" variant="outlined" />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Role</Button>
      </Box>
      <DataTable
        columns={columns}
        data={roles}
        loading={loading}
        searchPlaceholder="Search roles..."
        emptyMessage="No roles found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedRole ? 'Edit Role' : 'Add Role'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedRole ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Permissions ({formData.permissions.length} selected)
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {Object.entries(permissionCategories).map(([category, perms]) => {
                const allSelected = perms.every((p) => formData.permissions.includes(p.code))
                const someSelected = perms.some((p) => formData.permissions.includes(p.code))
                return (
                  <Box key={category} mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={() => toggleCategory(perms)}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedCategories((prev) => ({
                          ...prev,
                          [category]: !prev[category],
                        }))}
                      >
                        {category} ({perms.filter((p) => formData.permissions.includes(p.code)).length}/{perms.length})
                      </Typography>
                    </Box>
                    <Box pl={4}>
                      {perms.map((perm) => (
                        <FormControlLabel
                          key={perm.code}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.permissions.includes(perm.code)}
                              onChange={() => togglePermission(perm.code)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {perm.name} <Typography component="span" variant="caption" color="text.secondary">({perm.code})</Typography>
                            </Typography>
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// Main Administration Page
const Administration = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  // Determine active tab based on URL
  const activeTab = useMemo(() => {
    if (location.pathname.includes('/admin/departments')) return 1
    if (location.pathname.includes('/admin/roles')) return 2
    return 0 // Default to users
  }, [location.pathname])

  const handleTabChange = (_, newValue) => {
    const routes = ['/admin/users', '/admin/departments', '/admin/roles']
    navigate(routes[newValue])
  }

  // Redirect non-admins
  if (!user?.is_superuser && !user?.is_staff) {
    return (
      <Box>
        <PageHeader
          title="Administration"
          subtitle="Access denied"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Administration' },
          ]}
        />
        <Card>
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              You need administrator privileges to access this section.
            </Typography>
          </Box>
        </Card>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Administration"
        subtitle="Manage users, departments, and roles"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Administration' },
        ]}
      />

      <Card>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Person />} iconPosition="start" label="Users" />
          <Tab icon={<Business />} iconPosition="start" label="Departments" />
          <Tab icon={<Security />} iconPosition="start" label="Roles" />
        </Tabs>

        {activeTab === 0 && <UsersTab />}
        {activeTab === 1 && <DepartmentsTab />}
        {activeTab === 2 && <RolesTab />}
      </Card>
    </Box>
  )
}

export default Administration
